import React, { Ref } from "react";
import { useCallback } from "react";
import get from "lodash/get";
import MenuItem from "@material-ui/core/MenuItem";
import { TextFieldProps } from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import {
  FieldTitle,
  useTranslate,
  ChoicesInputProps,
  useChoices,
} from "ra-core";
import {
  ClassesOverride,
  InputHelperText,
  Labeled,
  LinearProgress,
  ResettableTextField,
  sanitizeInputRestProps,
  SupportCreateSuggestionOptions,
} from "react-admin";
import { Controller, FieldValues, useFormContext } from "react-hook-form";
import { HookFormInputProps } from "./HookFormInputProps";
import { resettableStyles } from "ra-ui-materialui/lib/input/ResettableTextField";

declare module "react" {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

/**
 * An Input component for a select box, using an array of objects for the options
 *
 * Pass possible options as an array of objects in the 'choices' attribute.
 *
 * By default, the options are built from:
 *  - the 'id' property as the option value,
 *  - the 'name' property as the option text
 * @example
 * const choices = [
 *    { id: 'M', name: 'Male' },
 *    { id: 'F', name: 'Female' },
 * ];
 * <SelectInput source="gender" choices={choices} />
 *
 * You can also customize the properties to use for the option name and value,
 * thanks to the 'optionText' and 'optionValue' attributes.
 * @example
 * const choices = [
 *    { _id: 123, full_name: 'Leo Tolstoi', sex: 'M' },
 *    { _id: 456, full_name: 'Jane Austen', sex: 'F' },
 * ];
 * <SelectInput source="author_id" choices={choices} optionText="full_name" optionValue="_id" />
 *
 * `optionText` also accepts a function, so you can shape the option text at will:
 * @example
 * const choices = [
 *    { id: 123, first_name: 'Leo', last_name: 'Tolstoi' },
 *    { id: 456, first_name: 'Jane', last_name: 'Austen' },
 * ];
 * const optionRenderer = choice => `${choice.first_name} ${choice.last_name}`;
 * <SelectInput source="author_id" choices={choices} optionText={optionRenderer} />
 *
 * `optionText` also accepts a React Element, that will be cloned and receive
 * the related choice as the `record` prop. You can use Field components there.
 * @example
 * const choices = [
 *    { id: 123, first_name: 'Leo', last_name: 'Tolstoi' },
 *    { id: 456, first_name: 'Jane', last_name: 'Austen' },
 * ];
 * const FullNameField = ({ record }) => <span>{record.first_name} {record.last_name}</span>;
 * <SelectInput source="gender" choices={choices} optionText={<FullNameField />}/>
 *
 * The choices are translated by default, so you can use translation identifiers as choices:
 * @example
 * const choices = [
 *    { id: 'M', name: 'myroot.gender.male' },
 *    { id: 'F', name: 'myroot.gender.female' },
 * ];
 *
 * However, in some cases (e.g. inside a `<ReferenceInput>`), you may not want
 * the choice to be translated. In that case, set the `translateChoice` prop to false.
 * @example
 * <SelectInput source="gender" choices={choices} translateChoice={false}/>
 *
 * The object passed as `options` props is passed to the material-ui <Select> component
 *
 * You can disable some choices by providing a `disableValue` field which name is `disabled` by default
 * @example
 * const choices = [
 *    { id: 123, first_name: 'Leo', last_name: 'Tolstoi' },
 *    { id: 456, first_name: 'Jane', last_name: 'Austen' },
 *    { id: 976, first_name: 'William', last_name: 'Rinkerd', disabled: true },
 * ];
 *
 * @example
 * const choices = [
 *    { id: 123, first_name: 'Leo', last_name: 'Tolstoi' },
 *    { id: 456, first_name: 'Jane', last_name: 'Austen' },
 *    { id: 976, first_name: 'William', last_name: 'Rinkerd', not_available: true },
 * ];
 * <SelectInput source="gender" choices={choices} disableValue="not_available" />
 *
 */
const HookFormSelectInputComponent = <
  TRecord extends FieldValues = FieldValues
>(
  { classes: ____, ...props }: SelectInputProps<TRecord>,
  ref: Ref<HTMLElement>
) => {
  const {
    allowEmpty,
    choices = [],
    classes: classesOverride,
    className,
    create,
    createLabel,
    createValue,
    disableValue = "disabled",
    emptyText = "",
    emptyValue = "",
    format,
    helperText,
    label,
    loaded,
    loading,
    margin = "dense",
    onBlur,
    onChange,
    onCreate,
    onFocus,
    options = {},
    optionText = "name",
    optionValue = "id",
    parse,
    resource: __,
    source: _,
    translateChoice = true,
    validate,
    control,
    name,
    defaultValue,
    rules,
    variant,
    ...rest
  } = props;
  const translate = useTranslate();
  const classes = useStyles(props);

  const { setValue } = useFormContext();

  const { getChoiceText, getChoiceValue } = useChoices({
    optionText,
    optionValue,
    translateChoice,
  });

  const renderEmptyItemOption = useCallback(() => {
    return React.isValidElement(emptyText)
      ? React.cloneElement(emptyText)
      : emptyText === ""
      ? " " // em space, forces the display of an empty line of normal height
      : translate(emptyText, { _: emptyText });
  }, [emptyText, translate]);

  const renderMenuItemOption = useCallback((choice) => getChoiceText(choice), [
    getChoiceText,
  ]);

  if (loading) {
    return (
      <Labeled
        label={label}
        source={name}
        className={className}
        isRequired={!!rules?.required}
        margin={margin}
      >
        <LinearProgress />
      </Labeled>
    );
  }

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      name={name}
      render={(renderProps) => (
        <>
          <ResettableTextField
            {...renderProps.field}
            onChange={(e) => {
              setValue(e.target.name, e.target.value, {
                shouldDirty: true,
                shouldValidate: false,
              });
              renderProps.field.onBlur();
            }}
            ref={ref}
            inputRef={renderProps.field.ref}
            select
            label={
              label !== "" &&
              label !== false && (
                <FieldTitle
                  label={label}
                  source={name}
                  isRequired={!!rules?.required}
                />
              )
            }
            className={`${classes.input} ${className}`}
            clearAlwaysVisible
            error={
              !!(
                renderProps.fieldState.isTouched && renderProps.fieldState.error
              )
            }
            helperText={
              rules?.required ? (
                <InputHelperText
                  touched={renderProps.fieldState.isTouched}
                  error={(renderProps.fieldState.error as any)?.message}
                  helperText={helperText}
                />
              ) : (
                (renderProps.fieldState.error as any)?.message ?? " "
              )
            }
            margin={margin}
            {...options}
            {...sanitizeRestProps(rest)}
            variant={variant}
          >
            {allowEmpty ? (
              <MenuItem
                value={emptyValue}
                key="null"
                aria-label={translate("ra.action.clear_input_value")}
                title={translate("ra.action.clear_input_value")}
              >
                {renderEmptyItemOption()}
              </MenuItem>
            ) : null}
            {choices.map((choice) => (
              <MenuItem
                key={getChoiceValue(choice)}
                value={getChoiceValue(choice)}
                disabled={get(choice, disableValue)}
              >
                {renderMenuItemOption(choice)}
              </MenuItem>
            ))}
          </ResettableTextField>
        </>
      )}
    />
  );
};

const sanitizeRestProps = ({
  addLabel,
  afterSubmit,
  allowNull,
  beforeSubmit,
  choices,
  className,
  crudGetMatching,
  crudGetOne,
  data,
  filter,
  filterToQuery,
  formatOnBlur,
  isEqual,
  limitChoicesToValue,
  multiple,
  name,
  pagination,
  perPage,
  ref,
  reference,
  refetch,
  render,
  setFilter,
  setPagination,
  setSort,
  sort,
  subscription,
  type,
  validateFields,
  validation,
  value,
  ...rest
}: any) => sanitizeInputRestProps(rest);

const useStyles = makeStyles(
  (theme) => ({
    ...resettableStyles,
    input: {
      minWidth: theme.spacing(20),
    },
  }),
  { name: "RaSelectInput" }
);

export interface SelectInputProps<TRecord>
  extends ChoicesInputProps<TextFieldProps>,
    Omit<HookFormInputProps<TRecord>, "label">,
    Omit<SupportCreateSuggestionOptions, "handleChange">,
    Omit<
      TextFieldProps,
      "label" | "helperText" | "classes" | "defaultValue" | "name" | "ref"
    > {
  classes?: ClassesOverride<typeof useStyles>;
}

export const HookFormSelectInput = React.forwardRef(
  HookFormSelectInputComponent
);
